// src/pages/Analyze-Products.jsx
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Trash2, Search, ChevronDown, RotateCcw, Loader2 } from "lucide-react";
import { useAuth } from "@clerk/clerk-react";
import toast from "react-hot-toast";
import { matchSorter } from "match-sorter";
import { useProductStore } from "../store/useProductStore";
import Loader from "../components/Loader";

const placeholderImage =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="144" height="96" viewBox="0 0 144 96">
      <rect width="144" height="96" rx="12" fill="#111124"/>
      <rect x="1" y="1" width="142" height="94" rx="11" fill="none" stroke="rgba(255,255,255,0.08)"/>
      <circle cx="54" cy="40" r="10" fill="#FC2783" opacity="0.8"/>
      <path d="M24 72l24-24 18 18 14-14 40 20H24z" fill="rgba(255,255,255,0.12)"/>
      <text x="72" y="84" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" fill="rgba(255,255,255,0.45)">
        No preview
      </text>
    </svg>
  `);

/* ------------------------------ Helpers ------------------------------ */

/**
 * Normalize user-input URLs so we can accept:
 * - stifler.ro/products/xxx
 * - www.stifler.ro/products/xxx
 * - https://stifler.ro/products/xxx
 * - http://stifler.ro/products/xxx
 *
 * Rules:
 * - Trim whitespace
 * - If missing protocol, default to https://
 * - Leave existing http/https untouched
 */
function normalizeUrlInput(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";

  // Already has a scheme
  if (/^https?:\/\//i.test(s)) return s;

  // Protocol-relative (rare, but safe)
  if (s.startsWith("//")) return `https:${s}`;

  // Default
  return `https://${s}`;
}

const hostFromUrl = (url) => {
  const raw = String(url || "").trim();
  if (!raw) return "";
  try {
    const u = new URL(raw);
    return u.hostname.replace(/^www\./i, "");
  } catch {
    try {
      const u2 = new URL(normalizeUrlInput(raw));
      return u2.hostname.replace(/^www\./i, "");
    } catch {
      return "";
    }
  }
};

/**
 * Canonical "exclude" set for non-generated buckets.
 * Anything with these feature keys is treated as imported / inputs.
 */
function isPendingProjectRow(project) {
  const title = String(project?.product_name || "").trim().toLowerCase();
  return title === "importing…" || title === "importing...";
}
const EXCLUDE_FEATURES = new Set([
  "analyzed",
  "analysis",
  "scrape",
  "scraped",
  "raw",
  "input",
  "inputs",
  "source",
]);

/**
 * Only count assets that come from Generate-page tools.
 * These keys mirror the canonical feature keys used in ProjectDetails FEATURE_LABELS.
 */
const ALLOWED_FEATURE_KEYS = new Set([
  // Ads
  "product-avatar-video",
  "ad-marketing-video",
  "product-avatar-image",
  "photoshoot-anyshoot",
  "background-replacer",
  "ad-creative",

  // Improvements
  "remove-bg",
  "color-restoration",
  "enhancer",
  "outpaint",
  "light-fix",
  "object-replacer",
  "object-remover",
  "old-photos-restoration",
  "upscaler",
]);

/** Normalize various BE/legacy keys → canonical feature keys (fallback to raw key, NOT type) */
function normalizeFeatureKeyForCount(a) {
  const raw = a?.metadata?.feature ?? a?.feature ?? a?.source ?? "";

  const s = String(raw || "").toLowerCase().trim();
  if (!s) return "";

  // If it's explicitly an excluded bucket, pass through so we can drop later
  if (EXCLUDE_FEATURES.has(s)) return s;

  // Ad / generator mappings (same spirit as ProjectDetails)
  if (s.includes("product_avatar_video")) return "product-avatar-video";
  if (s.includes("product_avatar_image")) return "product-avatar-image";
  if (s.includes("ad_marketing") || s.includes("marketing_video"))
    return "ad-marketing-video";
  if (s.includes("photoshoot_anyshoot")) return "photoshoot-anyshoot";

  if (
    s.includes("background_replacer") ||
    s.includes("background-generator") ||
    s.includes("photoshoot_images")
  )
    return "background-replacer";

  if (s.includes("adcreative") || s === "adcreative" || s === "ad_creative")
    return "ad-creative";

  if (s.includes("remove") && s.includes("bg")) return "remove-bg";
  if (s.includes("remove-background")) return "remove-bg";

  if (s.includes("color_restor") || s.includes("color-restor"))
    return "color-restoration";
  if (s.includes("enhanc")) return "enhancer";
  if (s.includes("outpaint") || s.includes("extend")) return "outpaint";
  if (s.includes("light_fix") || s.includes("lightfix")) return "light-fix";
  if (s.includes("object_replacer")) return "object-replacer";
  if (s.includes("object_removed") || s.includes("object_remover"))
    return "object-remover";
  if (s.includes("old_photo") || s.includes("old-photos"))
    return "old-photos-restoration";
  if (s.includes("upscal")) return "upscaler";

  // Fallback: treat unknown but non-excluded string as its own feature key
  return s;
}

/** Is this feature key considered "generated" vs imported/scraped? */
function isGeneratedFeatureKeyForCount(key) {
  if (!key) return false;
  const s = String(key).toLowerCase();
  if (EXCLUDE_FEATURES.has(s)) return false;
  // Only include assets from known Generate tools
  return ALLOWED_FEATURE_KEYS.has(s);
}

/** Flatten assetsByFeature into a flat array and attach feature when missing */
function flattenAssetsByFeatureForCount(abf) {
  const out = [];
  if (!abf || typeof abf !== "object") return out;
  for (const [featureKey, rows] of Object.entries(abf)) {
    if (!Array.isArray(rows)) continue;
    for (const r of rows) {
      if (!r?.metadata?.feature) {
        // eslint-disable-next-line no-param-reassign
        r.metadata = { ...(r.metadata || {}), feature: featureKey };
      }
      out.push(r);
    }
  }
  return out;
}

/** Map raw asset row to a unified shape for counting; skips non-generated */
function toUnifiedItemForCount(a) {
  const featureKey = normalizeFeatureKeyForCount(a);
  if (!isGeneratedFeatureKeyForCount(featureKey)) return null;

  const url = a.url || a.image_url || a.video_url || "";
  if (!url) return null;

  const isVideo =
    a.type === "video" ||
    a.resource_type === "video" ||
    a.kind === "video" ||
    a?.metadata?.type === "video";

  return {
    id: a.id || `${featureKey}-${url}`,
    type: isVideo ? "video" : "image",
    featureKey,
    url,
    created_at: a.created_at || a.createdAt || null,
  };
}

/**
 * Count ONLY generated assets from /api/projects/:id payload.
 * This matches the ProjectDetails logic (assets + assetsByFeature + cloudflare + adcreatives via assets),
 * filtered so we only count assets produced by Generate-page tools.
 */
function countFromProjectDetails(payload) {
  const flatAssets = Array.isArray(payload?.assets) ? payload.assets : [];
  const fromByFeature = flattenAssetsByFeatureForCount(payload?.assetsByFeature);
  const cloudflareAssets =
    payload?.cloudflareAssets || payload?.cloudflare_assets || [];

  const combinedRaw = [...flatAssets, ...fromByFeature, ...cloudflareAssets];

  const dedupMap = new Map();
  combinedRaw.forEach((row) => {
    const item = toUnifiedItemForCount(row);
    if (!item) return;
    const key = `${item.featureKey}-${item.id || item.url}`;
    if (!dedupMap.has(key)) dedupMap.set(key, item);
  });

  let images = 0;
  let videos = 0;

  for (const item of dedupMap.values()) {
    if (item.type === "video") videos += 1;
    else images += 1;
  }

  return { images, videos, total: images + videos };
}

/** Same-size dropdown as ProjectDetails (no label) */
function SortDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target))
        setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const opts = [
    { value: "Newest first", label: "Newest first" },
    { value: "Oldest first", label: "Oldest first" },
  ];
  const current = opts.find((o) => o.value === value)?.label || "Newest first";

  return (
    <div className="relative" ref={rootRef} style={{ width: 224 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={[
          "group relative flex items-center justify-between gap-2",
          "rounded-2xl px-3 h-10",
          "bg-[#17172a] text-white text-sm",
          "border border-[#343454]",
          "shadow-[inset_0_0_0_1px_rgba(255,255,255,0.02)]",
          "transition-all focus:outline-none",
          open
            ? "ring-2 ring-pink-500/40 border-pink-500/50"
            : "hover:border-pink-500/30",
        ].join(" ")}
        style={{ width: 224 }}
      >
        <span className="truncate">{current}</span>
        <ChevronDown
          size={18}
          className={`shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
        <span className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-white/5" />
      </button>

      {open && (
        <ul
          role="listbox"
          tabIndex={-1}
          className={[
            "absolute left-0 top-full mt-2",
            "z-50 rounded-2xl",
            "bg-[#151528]/95 backdrop-blur-sm",
            "border border-[#343454]",
            "shadow-2xl shadow-black/40",
            "max-h-72 overflow-auto",
            "animate-[fadeIn_120ms_ease-out]",
          ].join(" ")}
          style={{ width: 224 }}
        >
          {opts.map((o) => {
            const isSel = o.value === value;
            return (
              <li
                key={o.value}
                role="option"
                aria-selected={isSel}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={[
                  "cursor-pointer px-3 py-2 text-sm select-none",
                  "flex items-center justify-between",
                  isSel
                    ? "bg-pink-600/15 text-pink-300"
                    : "hover:bg-[#232340]",
                ].join(" ")}
              >
                <span className="truncate">{o.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------ Page ------------------------------ */

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortChoice, setSortChoice] = useState("Newest first");
  const [isReimporting, setIsReimporting] = useState({}); // { [projectId]: true }

  // Infinite scroll
  const [visibleCount, setVisibleCount] = useState(20);
  const sentinelRef = useRef(null);

  // Quick Analyze
  const [quickUrl, setQuickUrl] = useState("");
  const [quickLoading, setQuickLoading] = useState(false);

  // URL quota (per user / per billing cycle)
  const [urlQuota, setUrlQuota] = useState({
    loading: true,
    unlimited: false,
    remaining: 0,
    used: 0,
    baseCap: 0,
    urlsExtra: 0,
    period_start: null,
    period_end: null,
  });

  // Page-level loading for initial data fetch (used only for the Project List card)
  const [pageLoading, setPageLoading] = useState(true);

  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { setSelectedProduct } = useProductStore();

  // Fetch URL quota for analyze (same pattern as image quotas)
  const fetchUrlQuota = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/analyze/quota`,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );
      const data = res.data || {};
      setUrlQuota((prev) => ({
        ...prev,
        loading: false,
        unlimited: !!data.unlimited,
        remaining: data.remaining ?? 0,
        used: data.used ?? 0,
        baseCap: data.baseCap ?? 0,
        urlsExtra: data.urlsExtra ?? 0,
        period_start: data.period_start || null,
        period_end: data.period_end || null,
      }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to load URL quota:", err?.message || err);
      setUrlQuota((prev) => ({ ...prev, loading: false }));
    }
  }, [getToken]);

  // Derived quota values
  const canAnalyze =
    urlQuota.loading ||
    urlQuota.unlimited ||
    (Number(urlQuota.remaining) || 0) > 0;

  const totalUrls = urlQuota.unlimited
    ? Infinity
    : (Number(urlQuota.baseCap) || 0) + (Number(urlQuota.urlsExtra) || 0);

  const remainingUrls = urlQuota.unlimited
    ? Infinity
    : Number(urlQuota.remaining) || 0;
  const usedUrls = urlQuota.unlimited ? 0 : Number(urlQuota.used) || 0;

  const showUrlBar =
    !urlQuota.loading &&
    !urlQuota.unlimited &&
    Number.isFinite(totalUrls) &&
    totalUrls > 0;

  const urlsPct = showUrlBar
    ? Math.min(100, Math.max(0, Math.round((usedUrls / totalUrls) * 100)))
    : 0;

  // Fetch projects with counts from assets table (filtered to Generate features only)
  const loadProjects = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects`,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );
      const list = Array.isArray(res.data) ? res.data : [];

      // Enrich each project with image/video counts by reading /api/projects/:id
      const withCounts = await Promise.all(
        list.map(async (p) => {
          try {
            const details = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/projects/${p.id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            const { images, videos, total } = countFromProjectDetails(
              details.data || {}
            );
            return {
              ...p,
              images_count: images,
              videos_count: videos,
              assets_total: total,
            };
          } catch {
            return { ...p, images_count: 0, videos_count: 0, assets_total: 0 };
          }
        })
      );

      setProjects(withCounts);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch projects:", err);
      toast.error("Failed to fetch projects");
    }
  }, [getToken]);

  // Initial page load: show loader inside Project List card while projects + quota are being fetched
  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        setPageLoading(true);
        await Promise.all([loadProjects(), fetchUrlQuota()]);
      } finally {
        if (isMounted) setPageLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [loadProjects, fetchUrlQuota]);

  const handleProjectSelect = async (project) => {
    setSelectedProduct(project);
    navigate(`/project/${project.id}`);
  };

  const handleGenerateForProject = (project) => {
    setSelectedProduct(project);
    navigate("/generate");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      const token = await getToken();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );
      setProjects((prev) => prev.filter((p) => p.id !== id));
      toast.success("Project deleted");
      window.dispatchEvent(new Event("projectListUpdated"));

      // ✅ Keep quota UI accurate after deletes (and BE no longer "refunds" on delete)
      await fetchUrlQuota();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Delete failed:", err);
      toast.error("Delete failed");
    }
  };

  // Poll analyze status (auto-refresh token on 401 during long polls)
  const pollAnalyze = async (taskId, token) => {
    const maxTries = 60;
    const intervalMs = 5000;

    let tokenLocal = token;

    for (let i = 0; i < maxTries; i += 1) {
      try {
        const q = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/analyze/query`,
          {
            params: { taskId },
            headers: { Authorization: `Bearer ${tokenLocal}` },
          }
        );
        const status = q.data?.status;
        if (status === "success") return true;
      } catch (err) {
        // If the Clerk token expires mid-poll, refresh and continue
        if (err?.response?.status === 401) {
          try {
            // eslint-disable-next-line no-await-in-loop
            tokenLocal = await getToken();
          } catch (_) {
            // ignore; next loop will likely fail again, but we don't hard-stop
          }
        }
        // ignore transient errors
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    return false;
  };

  const handleReimport = async (project) => {
    const saved = project?.url || project?.product_url;
    const productUrl = normalizeUrlInput(saved);

    if (!productUrl) {
      toast.error("Missing saved product URL for this project");
      return;
    }

    try {
      setIsReimporting((s) => ({ ...s, [project.id]: true }));
      const token = await getToken();

      const submit = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/analyze`,
        { url: productUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const taskId = submit.data?.taskId;
      if (!taskId) {
        toast.error("Failed to start re-import");
        setIsReimporting((s) => ({ ...s, [project.id]: false }));
        return;
      }

      toast.success("Re-import started");

      // refresh immediately so row switches to pending/importing state
      await loadProjects();
      window.dispatchEvent(new Event("projectListUpdated"));

      const ok = await pollAnalyze(taskId, token);

      await loadProjects();
      window.dispatchEvent(new Event("projectListUpdated"));

      if (ok) {
        toast.success("Re-import completed");
      } else {
        toast.error("Re-import timed out");
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Re-import failed:", err);

      if (
        err?.response?.status === 402 &&
        err.response?.data?.code === "urls_quota_exceeded"
      ) {
        toast.error(
          "You’ve reached your product pages limit. Please upgrade your plan."
        );
        await fetchUrlQuota();
      } else {
        toast.error("Re-import failed");
      }
    } finally {
      setIsReimporting((s) => ({ ...s, [project.id]: false }));
    }
  };

  // Quick Analyze: add URL and start analysis from Dashboard
  const handleQuickAnalyze = async () => {
    const normalized = normalizeUrlInput(quickUrl);

    if (!normalized) {
      toast.error("Please input a product URL");
      return;
    }

    if (normalized !== quickUrl.trim()) {
      setQuickUrl(normalized);
    }

    if (!canAnalyze) {
      window.location.assign("https://pixelads.ai/pricing");
      return;
    }

    try {
      setQuickLoading(true);
      const token = await getToken();

      const submit = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/analyze`,
        { url: normalized },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const taskId = submit.data?.taskId;

      if (!taskId) {
        toast.error("Failed to start analysis");
        setQuickLoading(false);
        return;
      }

      // ✅ Refresh immediately so the backend-created pending row appears in the table
      await loadProjects();
      window.dispatchEvent(new Event("projectListUpdated"));
      setVisibleCount(20);
      await fetchUrlQuota();

      setQuickUrl("");
      setQuickLoading(false);
      toast.success("Import queued. You’ll see the project appear below shortly.");

      // ✅ Keep polling in background, but do not block the UI
      (async () => {
        const ok = await pollAnalyze(taskId, token);

        if (ok) {
          await loadProjects();
          window.dispatchEvent(new Event("projectListUpdated"));
          await fetchUrlQuota();
          toast.success("Import completed. Project list updated.");
        } else {
          await loadProjects();
          window.dispatchEvent(new Event("projectListUpdated"));
          await fetchUrlQuota();
          toast.error("Import timed out");
        }
      })();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Analyze failed:", err);

      if (
        err?.response?.status === 402 &&
        err.response?.data?.code === "urls_quota_exceeded"
      ) {
        toast.error(
          "You’ve reached your product pages limit. Please upgrade your plan."
        );
        await fetchUrlQuota();
        window.location.assign("https://pixelads.ai/pricing");
      } else if (err?.response?.status === 401) {
        toast.error("Session expired. Please try again.");
      } else {
        toast.error("Import failed");
      }

      setQuickLoading(false);
    }
  };

  // Search + sort for list view
  const rows = useMemo(() => {
    const searched = matchSorter(projects, searchQuery, {
      keys: [(item) => item.product_name || "Untitled Product"],
      threshold: matchSorter.rankings.CONTAINS,
    });

    const sorted = searched.slice().sort((a, b) => {
      const da = new Date(a.created_at || 0).getTime();
      const db = new Date(b.created_at || 0).getTime();
      return sortChoice === "Newest first" ? db - da : da - db;
    });

    return sorted;
  }, [projects, searchQuery, sortChoice]);

  // Infinite scroll: observer to increase visibleCount
  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const obs = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setVisibleCount((v) => Math.min(v + 20, rows.length));
        }
      },
      { root: null, rootMargin: "200px", threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [rows.length]);

  const displayRows = rows.slice(0, visibleCount);

  return (
    <div className="w-full px-4 sm:px-8 py-8">
      <div className="max-w-[1600px] mx-auto space-y-8">
        {/* ---------------- Analyze card ---------------- */}
        <div className="bg-[#15152B] border border-[#2A2A40] rounded-3xl px-8 py-8 shadow-lg">
          {/* Header + quota pill + usage bar (mirrors Product Avatar Image quota UX) */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div>
              <h2 className="text-xl font-semibold text-white">
                Import product from shop
              </h2>
            </div>

            {!urlQuota.loading && (
              <div
                className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 border text-xs sm:text-sm ${
                  urlQuota.unlimited
                    ? "border-pink-600/60 bg-pink-600/10 text-pink-200"
                    : "border-[#2A2A40] bg-[#1C1C34] text-white/80"
                }`}
                title={
                  urlQuota.unlimited
                    ? "Unlimited product pages this cycle"
                    : "Product pages usage this billing cycle"
                }
              >
                {urlQuota.unlimited ? (
                  <span className="font-semibold">
                    Unlimited product URLs this cycle
                  </span>
                ) : (
                  <span>
                    <span className="font-semibold text-white">
                      {remainingUrls}
                    </span>{" "}
                    left of{" "}
                    <span className="font-semibold text-white">{totalUrls}</span>
                  </span>
                )}
              </div>
            )}
          </div>

          {!urlQuota.loading && (
            <div className="space-y-2 mb-4">
              {showUrlBar && (
                <div>
                  <div className="h-2 w-full bg-[#21213a] rounded-full overflow-hidden border border-[#2A2A40]">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${urlsPct}%`,
                        background:
                          "linear-gradient(90deg, rgba(252,39,131,0.9) 0%, rgba(252,39,131,0.7) 100%)",
                      }}
                    />
                  </div>
                  <div className="flex justify-between mt-1 text-[11px] text-white/60">
                    <span>Used: {usedUrls}</span>
                    <span>Total: {totalUrls}</span>
                  </div>
                </div>
              )}

              {!urlQuota.unlimited && !canAnalyze && (
                <button
                  type="button"
                  onClick={() => window.location.assign("https://pixelads.ai/pricing")}
                  className="text-[11px] sm:text-xs text-pink-300 hover:text-pink-200 underline decoration-pink-500/60 underline-offset-2"
                >
                  You’ve reached your product pages limit. Upgrade or add more
                  URLs on the Pricing page.
                </button>
              )}
            </div>
          )}

          {/* Row: input + button are vertically centered and same height */}
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="flex-1 w-full">
              <input
                type="text"
                inputMode="url"
                placeholder="Paste a product URL to import…"
                value={quickUrl}
                onChange={(e) => setQuickUrl(e.target.value)}
                className="w-full h-10 bg-[#1F1F2F] text-white rounded-xl px-4 border border-white/40 focus:outline-none focus:border-pink-500 transition"
                aria-label="Product URL to analyze"
              />
            </div>

            <div className="w-full sm:w-72">
              <button
                onClick={handleQuickAnalyze}
                disabled={quickLoading}
                className={[
                  "w-full h-10 rounded-xl font-semibold whitespace-nowrap transition",
                  "inline-flex items-center justify-center gap-2",
                  quickLoading
                    ? "bg-pink-700/50 border border-pink-600/40 text-white/80 cursor-not-allowed"
                    : !canAnalyze
                    ? "bg-[#2a2a3f] border border-red-500/60 text-red-200 hover:bg-[#2a2a3f] cursor-pointer"
                    : "bg-pink-600 hover:bg-pink-500 border border-pink-400/60 text-white",
                ].join(" ")}
              >
                {quickLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Importing…</span>
                  </>
                ) : !canAnalyze ? (
                  "Add more URLs on Pricing page"
                ) : (
                  "Import product"
                )}
              </button>
            </div>
          </div>

          <p className="mt-3 text-xs text-white/60">
            Paste a product page URL and we’ll import images, videos, and details
            for you.
          </p>

          <p className="mt-3 text-xs text-white/60">
            Example:{" "}
            <span className="whitespace-nowrap">
              yourshop.myshopify.com/products/coffee-mug
            </span>
          </p>
        </div>

        {/* --------------- Products list card --------------- */}
        <div className="bg-[#15152B] border border-[#2A2A40] rounded-3xl px-8 py-10 shadow-lg">
          <h1
            className="text-3xl font-bold text-white mb-2 transition-all duration-300 hover:text-pink-500 hover:drop-shadow-[0_0_10px_rgba(255,0,128,0.5)]"
            aria-label="Project list heading"
          >
            Project List
          </h1>

          {/* Sort (left) + Search (right) */}
          <div className="flex items-center justify-between gap-4 mb-6">
            <SortDropdown value={sortChoice} onChange={setSortChoice} />
            <div className="relative w-72">
              <input
                type="text"
                placeholder="Search projects…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-11 bg-[#232344] text-white rounded-xl px-4 pl-10 border border-pink-500/60 shadow-[0_0_0_1px_rgba(255,255,255,0.05)] focus:outline-none focus:border-pink-400 focus:ring-2 focus:ring-pink-500/40 transition"
                aria-label="Search projects"
              />
              <Search className="absolute left-3 top-3 h-5 w-5 text-pink-300" />
            </div>
          </div>

          {pageLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-2xl border border-[#2A2A40]">
                <table className="w-full text-sm text-left text-white/90">
                  <thead className="bg-[#1b1b32] text-white/70">
                    <tr>
                      <th className="px-4 py-3 w-16">#</th>
                      <th className="px-4 py-3 w-[88px]">Asset</th>
                      <th className="px-4 py-3">Title</th>
                      <th className="px-4 py-3">Host</th>
                      <th className="px-4 py-3 text-center">Assets</th>
                      <th className="px-4 py-3 text-center">Images</th>
                      <th className="px-4 py-3 text-center">Videos</th>
                      <th className="px-4 py-3">Created</th>
                      <th className="px-4 py-3 text-right w-[300px]">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                  {displayRows.map((p, idx) => {
                      const title = p.product_name || "Untitled Product";
                      const thumb =
                        typeof p.thumbnail_url === "string" && p.thumbnail_url.trim()
                          ? p.thumbnail_url.trim()
                          : placeholderImage;
                      const created = p.created_at
                        ? new Date(p.created_at).toLocaleString()
                        : "";
                      const host = p.url
                        ? hostFromUrl(p.url)
                        : p.product_url
                        ? hostFromUrl(p.product_url)
                        : "";
                      const total = Number(
                        p.assets_total ||
                          (p.images_count || 0) + (p.videos_count || 0)
                      );
                      const images = Number(p.images_count || 0);
                      const videos = Number(p.videos_count || 0);

                      const isPending = isPendingProjectRow(p);
                      const rowReimporting = !!isReimporting[p.id] || isPending;

                      return (
                        <tr
                          key={p.id}
                          className={`border-t border-[#2A2A40] ${
                            isPending ? "bg-[#1a1a30]" : "hover:bg-[#20203a]"
                          }`}
                        >
                          <td className="px-4 py-3 align-middle text-white/70">
                            {idx + 1}
                          </td>

                          <td className="px-4 py-3 align-middle">
                            {isPending ? (
                              <div className="w-[72px] h-[48px] rounded-lg border border-white/10 bg-[#111124] overflow-hidden relative">
                                <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-white/5 via-white/10 to-white/5" />
                              </div>
                            ) : (
                              <button
                                onClick={() => handleProjectSelect(p)}
                                className="block rounded-lg overflow-hidden border border-white/10 bg-[#111124] hover:ring-1 hover:ring-pink-500/40 transition"
                                title="Open project"
                              >
                                <img
                                  src={thumb}
                                  alt="thumb"
                                  loading="lazy"
                                  className="w-[72px] h-[48px] object-contain"
                                  onError={(e) => {
                                    if (
                                      e.currentTarget.dataset.fallbackApplied === "true"
                                    )
                                      return;
                                    e.currentTarget.dataset.fallbackApplied = "true";
                                    e.currentTarget.src = placeholderImage;
                                  }}
                                  draggable={false}
                                />
                              </button>
                            )}
                          </td>

                          <td className="px-4 py-3 align-middle">
                            {isPending ? (
                              <div className="space-y-2">
                                <div className="h-4 w-56 rounded bg-white/10 animate-pulse" />
                                <div className="h-3 w-40 rounded bg-white/5 animate-pulse" />
                              </div>
                            ) : (
                              <button
                                onClick={() => handleProjectSelect(p)}
                                className="text-left w-full"
                                title={title}
                              >
                                <div className="font-semibold text-white truncate max-w-[380px]">
                                  {title}
                                </div>
                              </button>
                            )}
                          </td>

                          <td className="px-4 py-3 align-middle text-white/70">
                            {isPending ? (
                              <div className="h-4 w-24 rounded bg-white/10 animate-pulse" />
                            ) : (
                              <span
                                className="truncate max-w-[160px] inline-block"
                                title={host}
                              >
                                {host}
                              </span>
                            )}
                          </td>

                          <td className="px-4 py-3 align-middle text-center">
                            {isPending ? (
                              <div className="mx-auto h-7 w-12 rounded-full bg-white/10 animate-pulse" />
                            ) : (
                              <span className="inline-flex items-center justify-center min-w-[48px] h-7 px-2 rounded-full bg-white/10 border border-white/15">
                                {total}
                              </span>
                            )}
                          </td>

                          <td className="px-4 py-3 align-middle text-center">
                            {isPending ? (
                              <div className="mx-auto h-7 w-12 rounded-full bg-pink-600/20 animate-pulse" />
                            ) : (
                              <span className="inline-flex items-center justify-center min-w-[48px] h-7 px-2 rounded-full bg-pink-600/20 border border-pink-500/40 text-pink-200">
                                {images}
                              </span>
                            )}
                          </td>

                          <td className="px-4 py-3 align-middle text-center">
                            {isPending ? (
                              <div className="mx-auto h-7 w-12 rounded-full bg-blue-600/20 animate-pulse" />
                            ) : (
                              <span className="inline-flex items-center justify-center min-w-[48px] h-7 px-2 rounded-full bg-blue-600/20 border border-blue-500/40 text-blue-200">
                                {videos}
                              </span>
                            )}
                          </td>

                          <td className="px-4 py-3 align-middle text-white/70">
                            {isPending ? (
                              <div className="h-4 w-28 rounded bg-white/10 animate-pulse" />
                            ) : (
                              created
                            )}
                          </td>

                          <td className="px-4 py-3 align-middle">
                            {isPending ? (
                              <div className="flex items-center justify-end gap-2">
  <span className="inline-flex items-center gap-2 px-3 h-9 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 text-xs font-semibold">
    <Loader2 size={14} className="animate-spin" />
    {isReimporting[p.id] ? "Re-importing..." : "Import pending..."}
  </span>
</div>
                            ) : (
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleGenerateForProject(p)}
                                  className="px-3 h-9 rounded-lg bg-pink-600/20 hover:bg-pink-600/30 border border-pink-500/40 text-pink-100 text-xs font-semibold transition"
                                  title="Go to Generate for this product"
                                >
                                  Generate
                                </button>

                                <button
                                  onClick={() => handleProjectSelect(p)}
                                  className="px-3 h-9 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white text-xs font-semibold transition"
                                  title="Open project"
                                >
                                  Open
                                </button>

                                <button
                                  onClick={() => handleReimport(p)}
                                  disabled={rowReimporting}
                                  className={`px-3 h-9 rounded-lg border text-xs font-semibold transition inline-flex items-center gap-1 whitespace-nowrap ${
                                    rowReimporting
                                      ? "bg-emerald-700/50 border-emerald-600/40 text-white/70 cursor-not-allowed"
                                      : "bg-emerald-600/20 hover:bg-emerald-600/30 border-emerald-500/40 text-emerald-100"
                                  }`}
                                  title="Re-import product data"
                                >
                                  {rowReimporting ? (
                                    <Loader2 size={14} className="animate-spin" />
                                  ) : (
                                    <RotateCcw size={14} />
                                  )}
                                  {rowReimporting
                                    ? "Re-importing..."
                                    : "Re-import"}
                                </button>

                                <button
                                  onClick={() => handleDelete(p.id)}
                                  className="px-3 h-9 rounded-lg bg-red-600/20 hover:bg-red-600/30 border border-red-500/40 text-red-200 text-xs font-semibold transition inline-flex items-center gap-1"
                                  title="Delete project"
                                >
                                  <Trash2 size={14} />
                                  Delete
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                    {displayRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={9}
                          className="px-4 py-8 text-center text-white/70"
                        >
                          No projects yet. Paste a product URL above to import one.
                        </td>
                      </tr>
                    )}

                    <tr>
                      <td colSpan={9}>
                        <div ref={sentinelRef} className="h-2 w-full" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {visibleCount < rows.length && (
                <div className="text-center text-white/60 text-xs mt-3">
                  Loading more…
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
